import type { IMenuItem } from '@Shared/models/IMenuItem';
import type { ESystem } from '@Shared/models/index.model';
import { PermissionManager } from './permission-manager.class';

export interface MenuPermissionEntry {
  id: string;
  parentID?: string;
  system: ESystem;
  link?: string;
  paths: string[];
  ancestors: string[];
}

/** Index children as well as navigation entries; never infer identity from label text. */
export function indexPermissionMenus(menus: IMenuItem[]): MenuPermissionEntry[] {
  const entries: MenuPermissionEntry[] = [];
  function walk(items: IMenuItem[], parent?: MenuPermissionEntry) {
    for (const menu of items || []) {
      if (!menu?.id) continue;
      const paths = [menu.link, ...(menu.routeAliases || [])].filter(Boolean);
      const entry: MenuPermissionEntry = {
        id: menu.id,
        parentID: parent?.id,
        system: menu.system || parent?.system,
        link: menu.link,
        paths: paths.length ? paths : parent?.paths || [],
        ancestors: parent ? [...parent.ancestors, parent.id] : [],
      };
      entries.push(entry);
      walk(menu.subs, entry);
      walk(
        Object.entries(menu.children || {}).map(([id, item]) => ({ ...item, id })),
        entry,
      );
    }
  }
  walk(menus);
  return entries;
}

export function resolvePermissionMenu(
  entries: MenuPermissionEntry[],
  url: string,
  system: ESystem,
  routeIDs: string[] = [],
): MenuPermissionEntry {
  const path = PermissionManager.normalizeURL(url);
  const score = (item: MenuPermissionEntry) =>
    Math.max(
      -1,
      ...item.paths
        .filter((link) => PermissionManager.matchesURL(path, link))
        .map((link) => PermissionManager.normalizeURL(link).length),
    );
  const scoped = entries.filter((item) => item.system === system);
  let selected: MenuPermissionEntry;
  for (const id of routeIDs) {
    if (selected?.id === id) continue;
    const candidates = scoped.filter(
      (item) => item.id === id && (!selected || item.ancestors.includes(selected.id)),
    );
    candidates.sort((a, b) => score(b) - score(a));
    if (candidates.length > 1 && score(candidates[0]) === score(candidates[1])) return undefined;
    selected = candidates[0];
    if (!selected) return undefined;
  }
  if (selected) return selected;
  // Child actions without route metadata must not compete with navigable pages.
  return scoped
    .filter((item) => item.link && score(item) >= 0)
    .sort((a, b) => score(b) - score(a) || b.ancestors.length - a.ancestors.length)[0];
}

/** Route metadata is authoritative; segment matching covers legacy route factories. */
export function isWritePermissionRoute(url: string, routePaths: string[] = [], operation?: string): boolean {
  if (operation === 'edit') return true;
  const query = new URLSearchParams((url.split('?')[1] || '').split('#')[0]);
  if (['edit', 'create', 'clone', 'generate'].includes((query.get('action') || '').toLowerCase()))
    return true;
  if (operation === 'view') return false;
  const paths = [...routePaths, PermissionManager.normalizeURL(url)];
  return paths.some((path) =>
    path
      .split('/')
      .some(
        (segment) =>
          /^(create|edit|clone|generate|modify|resume)(?:-|$)/i.test(segment) ||
          /^(createBatch|runBatch)$/i.test(segment) ||
          segment === 'run',
      ),
  );
}
